import React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { OneModal } from '@factorialco/f0-react/dist/experimental'
import { Check } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import {
  ComparisonFeature,
  ComparisonModalProps,
  MarketplacePricingCard,
} from 'modules/marketplace_modules/components/Revamp/types'

const generateComparisonFeatures = (plans: MarketplacePricingCard[]): ComparisonFeature[] => {
  const businessFeatures = new Set<string>()
  const enterpriseFeatures = new Set<string>()

  plans.forEach((plan) => {
    if (plan.perksExtended) {
      const features = plan.perksExtended
        .split('\n')
        .filter(Boolean)
        .map((f) => f.trim().replace(/,$/, ''))

      if (plan.name === 'Business') {
        features.forEach((feature) => businessFeatures.add(feature))
      } else if (plan.name === 'Enterprise') {
        features.forEach((feature) => enterpriseFeatures.add(feature))
      }
    }
  })

  const enterpriseOnlyFeatures = Array.from(enterpriseFeatures).filter(
    (f) => !businessFeatures.has(f)
  )
  const businessOnlyFeatures = Array.from(businessFeatures).filter(
    (f) => !enterpriseFeatures.has(f)
  )
  const sharedFeatures = Array.from(businessFeatures).filter((f) => enterpriseFeatures.has(f))

  const orderedFeatures = [...enterpriseOnlyFeatures, ...sharedFeatures, ...businessOnlyFeatures]

  return orderedFeatures.map((feature, index) => {
    const planAvailability: Record<string, boolean> = {}

    plans.forEach((plan) => {
      if (plan.name) {
        const planFeatures = plan.perksExtended
          ? plan.perksExtended
              .split('\n')
              .filter(Boolean)
              .map((f) => f.trim().replace(/,$/, ''))
          : []

        let hasFeature = planFeatures.includes(feature)

        if (plan.name === 'Enterprise' && businessFeatures.has(feature)) {
          hasFeature = true
        }

        planAvailability[plan.name] = hasFeature
      }
    })

    return {
      id: (index + 1).toString(),
      text: feature,
      planAvailability,
    }
  })
}

export const ComparisonModal: React.FC<ComparisonModalProps> = ({
  isOpen,
  onClose,
  plans = [],
}) => {
  const orderedPlans = [...plans].sort((a, b) => {
    if (a.name === 'Business' && b.name === 'Enterprise') return -1
    if (a.name === 'Enterprise' && b.name === 'Business') return 1
    return 0
  })

  const comparisonFeatures = generateComparisonFeatures(orderedPlans)
  if (plans.length === 0) {
    return (
      <OneModal isOpen={isOpen} onClose={onClose}>
        <OneModal.Header title="Compare plans" />
        <OneModal.Content>
          <Box padding="s16">
            <Text size="200" color="grey700">
              {i18n.t('marketplace.revamp.modal.no_plans_available')}
            </Text>
          </Box>
        </OneModal.Content>
      </OneModal>
    )
  }

  return (
    <OneModal isOpen={isOpen} onClose={onClose}>
      <OneModal.Header title={i18n.t('marketplace.revamp.modal.title')} />
      <OneModal.Content>
        <Box paddingX="s8">
          <Box
            flexDirection="row"
            padding="s16"
            border={{ bottom: { width: 's1', style: 'solid', color: 'grey200' } }}
          >
            <Box style={{ flex: '1' }}>
              <Text size="200" weight="semibold" color="grey700">
                {i18n.t('marketplace.revamp.modal.features')}
              </Text>
            </Box>
            {orderedPlans.map((plan) => (
              <Box key={plan.name} style={{ width: '120px' }} alignItems="center">
                <Text size="200" weight="semibold" color="grey700">
                  {plan.name}
                </Text>
              </Box>
            ))}
          </Box>

          {comparisonFeatures.map((feature) => (
            <Box
              key={feature.id}
              flexDirection="row"
              padding="s16"
              border={{ bottom: { width: 's1', style: 'solid', color: 'grey100' } }}
              alignItems="center"
            >
              <Box style={{ flex: '1' }}>
                <Text size="200" color="black">
                  {feature.text}
                </Text>
              </Box>
              {orderedPlans.map((plan) => (
                <Box
                  key={plan.name}
                  style={{ width: '120px' }}
                  alignItems="center"
                  justifyContent="center"
                >
                  {feature.planAvailability[plan.name || ''] ? (
                    <F0Icon icon={Check} size="md" color="default" />
                  ) : (
                    <>-</>
                  )}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </OneModal.Content>
    </OneModal>
  )
}
